import { createInternalReferral } from "../api/ServiceRequest";
import { confirmAppointment, getAppointment, linkScheduledAppointment } from "../../api/Scheduling/appointments";
import { getHold, extractHold, releaseHold } from "../../api/Scheduling/holds";

const recoveryKey = ({ organizationRef, sourceEncounterId, patientId }) =>
    `internal-referral:${organizationRef}:${sourceEncounterId}:${patientId}`;

export function hasPendingReferral(scope) {
    return !!sessionStorage.getItem(recoveryKey(scope));
}

export function referralPayloadForAppointment(appointment) {
    const context = appointment.bookingContext;
    const referral = context?.internalReferral;
    const patientId = appointment.patients?.find(p => p.role === "subject")?.patientRef;
    if (!referral || !patientId || !context.sourceEncounterId) throw new Error("This appointment has no internal referral details.");
    return {
        resourceType: "serviceRequest", internalReferral: true, schedulingVersion: 2,
        proceedWith: "SCHEDULE", referralType: "appointment", sourceEncounterId: context.sourceEncounterId,
        patient: { id: patientId, reference: `Patient/${patientId}` },
        organization: { id: appointment.organizationRef, reference: `Organization/${appointment.organizationRef}` },
        serviceLine: referral.serviceLine, reasonForReferral: context.reason,
        appointment: { appointmentId: appointment.appointmentId },
    };
}

export async function finishAppointmentReferral(appointment) {
    const payload = referralPayloadForAppointment(appointment);
    await linkScheduledAppointment({ organizationRef: appointment.organizationRef,
        sourceEncounterId: payload.sourceEncounterId, appointmentId: appointment.appointmentId });
    await createInternalReferral(payload);
    sessionStorage.removeItem(recoveryKey({ organizationRef: appointment.organizationRef,
        sourceEncounterId: payload.sourceEncounterId, patientId: payload.patient.id }));
    return appointment;
}

// Only identifiers are stored in the browser. Reviewed clinical details survive
// in V2 booking_context, including when confirmation's response is lost.
export async function recoverAppointmentReferral(scope, reuseHoldId) {
    const saved = sessionStorage.getItem(recoveryKey(scope));
    if (!saved) return null;
    const { holdId } = JSON.parse(saved);
    const hold = extractHold(await getHold({ organizationRef: scope.organizationRef, holdId, consistency: "strong" }), holdId);
    if (hold?.holdId !== holdId) throw new Error("The previous booking could not be verified.");
    if (hold?.status === "confirmed") {
        if (!hold.appointmentId) throw new Error("Could not resolve the previous appointment. Retry before booking again.");
        const response = await getAppointment({ organizationRef: scope.organizationRef, appointmentId: hold.appointmentId, consistency: "strong" });
        const appointment = response?.appointment;
        if (!appointment || appointment.organizationRef !== scope.organizationRef || appointment.appointmentId !== hold.appointmentId || appointment.bookingContext?.sourceEncounterId !== scope.sourceEncounterId ||
            !appointment.patients?.some(p => p.role === "subject" && p.patientRef === scope.patientId))
            throw new Error("The previous booking could not be verified.");
        return finishAppointmentReferral(appointment);
    }
    if (hold?.status === "active" && reuseHoldId === holdId) return null;
    if (hold?.status === "active") await releaseHold({ organizationRef: scope.organizationRef, holdId });
    else if (!["expired", "released"].includes(hold?.status)) throw new Error("Could not verify the previous booking. Retry before booking again.");
    sessionStorage.removeItem(recoveryKey(scope));
    return null;
}

export async function bookAppointmentReferral(booking, referralPayload) {
    const scope = { organizationRef: booking.organizationRef, sourceEncounterId: booking.sourceEncounterId, patientId: booking.patientRefs[0] };
    let recovered;
    try { recovered = await recoverAppointmentReferral(scope, booking.holdId); }
    catch (error) { error.referralRecovery = true; throw error; }
    if (recovered) return recovered;
    await createInternalReferral({ ...referralPayload, schedulingVersion: 2, operation: "validate" });
    sessionStorage.setItem(recoveryKey(scope), JSON.stringify({ holdId: booking.holdId }));
    let created;
    try {
        created = await confirmAppointment({ organizationRef: booking.organizationRef,
            holdId: booking.holdId, patientRefs: booking.patientRefs, bookingContext: booking.bookingContext,
            appointmentStatus: "booked" });
        const appointment = { ...booking, appointmentId: created.appointmentId,
            patients: booking.patientRefs.map((patientRef, i) => ({ patientRef, role: i === 0 ? "subject" : "attendee" })) };
        return await finishAppointmentReferral(appointment);
    } catch (error) {
        if (!created && !error.bookedAppointment && error.response?.status >= 400 && error.response.status < 500) {
            // A prior timed-out confirmation may still be committing. Releasing
            // the original active hold serializes against confirmation before
            // recovery state can safely be discarded.
            try {
                const recovered = await recoverAppointmentReferral(scope);
                if (recovered) return recovered;
                error.referralAborted = true;
            } catch { error.referralRecovery = true; }
            throw error;
        }
        // Confirmation can commit before a network failure. Keep the hold key
        // and reconcile it on retry rather than releasing it or booking again.
        error.referralRecovery = true;
        throw error;
    }
}
